import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { CodeBlock, CopyButton } from '@/components/code-block'
import { useSite } from '@/hooks/use-site'
import { Badge, Button } from '@blogging/ui'
import { cn } from '@blogging/utils'
import { ChevronDown, Lock } from 'lucide-react'
import { useState } from 'react'

const API_BASE = `${import.meta.env.VITE_API_URL}/api/public/v1`

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function MethodBadge({ method }: { method: 'GET' | 'POST' | 'PUT' | 'DELETE' }) {
  return (
    <span
      className={cn(
        'shrink-0 inline-flex px-2 py-0.5 rounded text-[11px] font-bold font-mono',
        method === 'GET' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        method === 'POST' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        method === 'PUT' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        method === 'DELETE' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      )}
    >
      {method}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Endpoint card
// ---------------------------------------------------------------------------

interface Param {
  name: string
  in: 'path' | 'query'
  type: string
  required?: boolean
  description: string
}

interface EndpointDef {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  path: string
  description: string
  params?: Param[]
  example: string
  response: string
}

function EndpointCard({ ep }: { ep: EndpointDef }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <MethodBadge method={ep.method} />
        <code className="text-sm font-mono flex-1 text-foreground truncate">{ep.path}</code>
        <span className="text-xs text-muted-foreground hidden md:block mr-2 shrink-0">
          {ep.description}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground shrink-0 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="border-t divide-y">
          <div className="px-4 py-3">
            <p className="text-sm text-muted-foreground">{ep.description}</p>
          </div>

          {ep.params && ep.params.length > 0 && (
            <div className="px-4 py-3 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Parameters
              </p>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="text-left px-3 py-2 font-medium">Name</th>
                      <th className="text-left px-3 py-2 font-medium">In</th>
                      <th className="text-left px-3 py-2 font-medium">Type</th>
                      <th className="text-left px-3 py-2 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ep.params.map((p) => (
                      <tr key={p.name} className="border-t">
                        <td className="px-3 py-2">
                          <code className="font-mono">{p.name}</code>
                          {p.required && (
                            <span className="ml-1 text-destructive">*</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{p.in}</td>
                        <td className="px-3 py-2 text-muted-foreground font-mono">{p.type}</td>
                        <td className="px-3 py-2 text-muted-foreground">{p.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="px-4 py-3 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Example Request
            </p>
            <CodeBlock code={ep.example} />
          </div>

          <div className="px-4 py-3 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Response
            </p>
            <CodeBlock code={ep.response} />
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

function Section({
  title,
  description,
  endpoints,
}: {
  title: string
  description: string
  endpoints: EndpointDef[]
}) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="space-y-2">
        {endpoints.map((ep) => (
          <EndpointCard key={ep.method + ep.path} ep={ep} />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type Tab = 'content' | 'admin'

export function SiteSettingsApiDocsPage() {
  const { site } = useSite()
  const [tab, setTab] = useState<Tab>('content')

  const base = API_BASE
  // The API identifies sites by their database ID, not the slug in the URL.
  const sid = site?.id ?? '{siteId}'
  const sitePath = `${base}/sites/${sid}`

  const sections = [
    {
      title: 'Site',
      description: 'Retrieve metadata and navigation for your site.',
      endpoints: [
        {
          method: 'GET' as const,
          path: `/sites/{siteId}`,
          description: 'Get site details including name, description, logo, and navigation items.',
          params: [
            { name: 'siteId', in: 'path' as const, type: 'string', required: true, description: 'Your site ID' },
          ],
          example: `curl ${sitePath} \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
          response: JSON.stringify(
            {
              site: {
                id: sid,
                name: 'My Blog',
                slug: 'my-blog',
                description: 'A blog about building in public',
                logo: 'https://...',
                icon: 'https://...',
                navigationItems: {
                  header: [{ label: 'Home', url: '/' }, { label: 'About', url: '/about' }],
                  footer: [{ label: 'Privacy', url: '/privacy' }],
                },
              },
            },
            null,
            2,
          ),
        },
      ],
    },
    {
      title: 'Posts',
      description: 'Access published posts with optional filtering.',
      endpoints: [
        {
          method: 'GET' as const,
          path: `/sites/{siteId}/posts`,
          description: 'List all published posts ordered by publish date (newest first).',
          params: [
            { name: 'siteId', in: 'path' as const, type: 'string', required: true, description: 'Your site ID' },
            { name: 'tag', in: 'query' as const, type: 'string', description: 'Filter by tag slug' },
            { name: 'page', in: 'query' as const, type: 'number', description: 'Page number (default: 1)' },
            { name: 'limit', in: 'query' as const, type: 'number', description: 'Items per page (default: 10, max: 100)' },
          ],
          example: `curl "${sitePath}/posts?tag=updates&limit=10" \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
          response: JSON.stringify(
            {
              posts: [
                {
                  id: 'clx123...',
                  title: 'Hello World',
                  slug: 'hello-world',
                  excerpt: 'My first post...',
                  featureImage: 'https://...',
                  publishedAt: '2026-02-01T10:00:00Z',
                  tags: [{ id: '...', name: 'Updates', slug: 'updates' }],
                },
              ],
              meta: { page: 1, limit: 10, total: 42, totalPages: 5 },
            },
            null,
            2,
          ),
        },
        {
          method: 'GET' as const,
          path: `/sites/{siteId}/posts/{slug}`,
          description: 'Get a single post by its slug with full HTML content.',
          params: [
            { name: 'siteId', in: 'path' as const, type: 'string', required: true, description: 'Your site ID' },
            { name: 'slug', in: 'path' as const, type: 'string', required: true, description: 'Post slug or ID' },
          ],
          example: `curl ${sitePath}/posts/hello-world \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
          response: JSON.stringify(
            {
              post: {
                id: 'clx123...',
                title: 'Hello World',
                slug: 'hello-world',
                html: '<p>Post content...</p>',
                excerpt: 'My first post...',
                featureImage: 'https://...',
                featureImageAlt: 'Description',
                publishedAt: '2026-02-01T10:00:00Z',
                updatedAt: '2026-02-05T15:30:00Z',
                tags: [{ id: '...', name: 'Updates', slug: 'updates' }],
                authors: [{ id: '...', name: 'John Doe', image: 'https://...' }],
              },
            },
            null,
            2,
          ),
        },
      ],
    },
    {
      title: 'Pages',
      description: 'Access published static pages.',
      endpoints: [
        {
          method: 'GET' as const,
          path: `/sites/{siteId}/pages`,
          description: 'List all published pages.',
          params: [
            { name: 'siteId', in: 'path' as const, type: 'string', required: true, description: 'Your site ID' },
          ],
          example: `curl ${sitePath}/pages \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
          response: JSON.stringify(
            {
              pages: [
                { id: 'clx123...', title: 'About', slug: 'about', updatedAt: '2026-02-01T10:00:00Z' },
              ],
            },
            null,
            2,
          ),
        },
        {
          method: 'GET' as const,
          path: `/sites/{siteId}/pages/{slug}`,
          description: 'Get a single page by its slug with full HTML content.',
          params: [
            { name: 'siteId', in: 'path' as const, type: 'string', required: true, description: 'Your site ID' },
            { name: 'slug', in: 'path' as const, type: 'string', required: true, description: 'Page slug or ID' },
          ],
          example: `curl ${sitePath}/pages/about \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
          response: JSON.stringify(
            {
              page: {
                id: 'clx123...',
                title: 'About',
                slug: 'about',
                html: '<p>Page content...</p>',
                featureImage: 'https://...',
                updatedAt: '2026-02-01T10:00:00Z',
              },
            },
            null,
            2,
          ),
        },
      ],
    },
    {
      title: 'Tags',
      description: 'Access tags used to organize posts.',
      endpoints: [
        {
          method: 'GET' as const,
          path: `/sites/{siteId}/tags`,
          description: 'List all tags for the site.',
          params: [
            { name: 'siteId', in: 'path' as const, type: 'string', required: true, description: 'Your site ID' },
          ],
          example: `curl ${sitePath}/tags \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
          response: JSON.stringify(
            {
              tags: [
                { id: 'clx123...', name: 'Updates', slug: 'updates', description: 'Product updates' },
              ],
            },
            null,
            2,
          ),
        },
        {
          method: 'GET' as const,
          path: `/sites/{siteId}/tags/{slug}`,
          description: 'Get a single tag by its slug.',
          params: [
            { name: 'siteId', in: 'path' as const, type: 'string', required: true, description: 'Your site ID' },
            { name: 'slug', in: 'path' as const, type: 'string', required: true, description: 'Tag slug' },
          ],
          example: `curl ${sitePath}/tags/updates \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
          response: JSON.stringify(
            {
              tag: {
                id: 'clx123...',
                name: 'Updates',
                slug: 'updates',
                description: 'Product updates and news',
                featureImage: 'https://...',
                metaTitle: 'Updates | My Blog',
                metaDescription: 'Latest updates from My Blog',
              },
            },
            null,
            2,
          ),
        },
      ],
    },
  ]

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">API Reference</h1>
          <p className="text-muted-foreground">
            Integrate your site content into any application using the REST API.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setTab('content')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === 'content'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            Content API
          </button>
          <button
            onClick={() => setTab('admin')}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === 'admin'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            Admin API
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
              Soon
            </Badge>
          </button>
        </div>

        {/* Content API */}
        {tab === 'content' && (
          <div className="space-y-8">
            {/* Base URL */}
            <div className="rounded-lg border bg-card p-5 space-y-3">
              <h3 className="text-sm font-semibold">Base URL</h3>
              <div className="flex items-center gap-2 bg-muted rounded-md px-3 py-2">
                <code className="text-sm font-mono flex-1 text-foreground">{base}</code>
                <CopyButton text={base} />
              </div>
              <div className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-2">
                <span className="text-xs text-muted-foreground">Your Site ID</span>
                <code className="text-sm font-mono flex-1 text-foreground">{sid}</code>
                <CopyButton text={sid} />
              </div>
            </div>

            {/* Authentication */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
              <h3 className="text-sm font-semibold">Authentication</h3>
              <p className="text-sm text-muted-foreground">
                Authentication is optional for public content but recommended. Include your API key
                as a Bearer token to enable usage tracking and higher rate limits.
              </p>
              <CodeBlock
                code={`curl ${sitePath}/posts \\\n  -H "Authorization: Bearer YOUR_API_KEY"`}
              />
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="border rounded-md p-3 space-y-1">
                  <p className="font-medium">Without API key</p>
                  <p className="text-muted-foreground">100 requests / hour</p>
                </div>
                <div className="border rounded-md p-3 space-y-1">
                  <p className="font-medium">With API key</p>
                  <p className="text-muted-foreground">1,000 requests / hour</p>
                </div>
              </div>
            </div>

            {/* Endpoint sections */}
            {sections.map((s) => (
              <Section key={s.title} {...s} />
            ))}

            {/* Errors */}
            <div className="rounded-lg border bg-card p-5 space-y-3">
              <h3 className="text-sm font-semibold">Errors</h3>
              <p className="text-sm text-muted-foreground">
                All errors return a JSON object with an <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">error</code> field.
              </p>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['400', 'Bad Request — Invalid parameters'],
                      ['401', 'Unauthorized — Invalid API key'],
                      ['404', 'Not Found — Resource does not exist'],
                      ['429', 'Too Many Requests — Rate limit exceeded'],
                      ['500', 'Server Error — Something went wrong'],
                    ].map(([status, desc]) => (
                      <tr key={status} className="border-t">
                        <td className="px-3 py-2 font-mono font-medium">{status}</td>
                        <td className="px-3 py-2 text-muted-foreground">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CodeBlock
                code={JSON.stringify(
                  { error: 'Not found', message: "Post with slug 'hello-world' not found" },
                  null,
                  2,
                )}
              />
            </div>
          </div>
        )}

        {/* Admin API — coming soon */}
        {tab === 'admin' && (
          <div className="rounded-lg border bg-card p-8 flex flex-col items-center text-center space-y-3">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              <Lock className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="font-semibold">Admin API — Coming Soon</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              The Admin API will allow you to programmatically manage posts, pages, tags,
              and site settings. Authentication will be required for all endpoints.
            </p>
            <Badge variant="secondary" className="mt-1">
              In Development
            </Badge>
          </div>
        )}
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
